import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useQuery } from '@tanstack/react-query';
import {
  bidModifiersQueryOptions,
  proximityBidModifiersQueryOptions,
  useDeviceBidModifierMutation,
  useLocationBidModifierMutation,
  useScheduleBidModifierMutation,
  useDeleteDeviceBidModifierMutation,
  useDeleteLocationBidModifierMutation,
  useDeleteScheduleBidModifierMutation,
  useCreateProximityBidModifierMutation,
  useDeleteProximityBidModifierMutation,
  adSchedulesQueryOptions,
} from '@/lib/query-options';
import type { Location } from '@/lib/types';
import { RadiusForm, type RadiusFormValue } from '@/components/business/business-locations/radius-form';
import { Trash2, Pencil, Plus, Smartphone, Monitor, Tablet, MapPin, Clock, SlidersHorizontal, AlertCircle, Radar } from 'lucide-react';
import { DataTable } from '@/components/glitch-ui/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ColumnDef } from '@tanstack/react-table';

interface BidAdjustmentsViewProps {
  campaignId: number;
  targetLocations: Location[];
  disabled?: boolean;
  /** Used by the proximity tab to wire the &lt;RadiusForm&gt; address-search to a business. */
  businessId?: number | null;
  /** Used to surface the Smart Bidding warning on the proximity tab. */
  biddingStrategyType?: string | null;
}

type DeviceType = 'MOBILE' | 'DESKTOP' | 'TABLET';
type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
type ModifierType = 'device' | 'location' | 'schedule' | 'proximity';

// Google Ads ignores positive/negative bid modifiers under Smart Bidding except -100%.
// Mirrors backend _SMART_BIDDING_STRATEGIES.
const SMART_BIDDING_STRATEGIES = new Set([
  'TARGET_CPA',
  'TARGET_ROAS',
  'MAXIMIZE_CONVERSIONS',
  'MAXIMIZE_CONVERSION_VALUE',
]);

const isSmartBidding = (strategy: string | null | undefined): boolean =>
  !!strategy && SMART_BIDDING_STRATEGIES.has(strategy.toUpperCase());

// Humanize the raw Google Ads enum value (e.g. MAXIMIZE_CONVERSIONS) so it
// reads naturally in user-facing copy.
const BIDDING_STRATEGY_LABELS: Record<string, string> = {
  MAXIMIZE_CONVERSIONS: 'Maximize Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Maximize Conversion Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_SPEND: 'Target Spend',
};
const formatBiddingStrategy = (strategy: string | null | undefined): string =>
  strategy ? BIDDING_STRATEGY_LABELS[strategy.toUpperCase()] ?? strategy : '';

const DEVICE_TYPES: DeviceType[] = ['MOBILE', 'DESKTOP', 'TABLET'];
const DAYS_OF_WEEK: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const formatDeviceType = (type: string): string => {
  return type.charAt(0) + type.slice(1).toLowerCase();
};

const formatDayOfWeek = (day: string): string => {
  return day.charAt(0) + day.slice(1).toLowerCase();
};

const formatModifierValue = (value: number): string => {
  if (value === 0) return '0%';
  const percentage = value * 100;
  return `${percentage > 0 ? '+' : ''}${percentage.toFixed(0)}%`;
};

const getDeviceIcon = (deviceType: string) => {
  switch (deviceType) {
    case 'MOBILE':
      return <Smartphone className='h-4 w-4 text-gray-500' />;
    case 'DESKTOP':
      return <Monitor className='h-4 w-4 text-gray-500' />;
    case 'TABLET':
      return <Tablet className='h-4 w-4 text-gray-500' />;
    default:
      return <Monitor className='h-4 w-4 text-gray-500' />;
  }
};

const getTypeIcon = (type: ModifierType) => {
  switch (type) {
    case 'device':
      return <Monitor className='h-4 w-4' />;
    case 'location':
      return <MapPin className='h-4 w-4' />;
    case 'schedule':
      return <Clock className='h-4 w-4' />;
    case 'proximity':
      return <Radar className='h-4 w-4' />;
  }
};

// Type pill styling
const getTypePillStyle = (type: ModifierType) => {
  switch (type) {
    case 'device':
      return 'bg-purple-50 text-purple-600';
    case 'location':
      return 'bg-green-50 text-green-600';
    case 'schedule':
      return 'bg-orange-50 text-orange-600';
    case 'proximity':
      return 'bg-blue-50 text-blue-600';
  }
};

interface UnifiedModifier {
  id: string;
  type: ModifierType;
  name: string;
  description: string;
  modifierValue: number;
  rawData: unknown;
  clippingInfo?: string;
  /** Optional small pill rendered alongside the (truncated) name. Used by
   *  proximity rows to surface the radius even when the address is long enough
   *  to truncate. */
  secondaryBadge?: string;
}

const getScheduleClippingInfo = (
  modifier: { day_of_week: string; start_hour: number; end_hour: number },
  adSchedules: Array<{ day_of_week: string; start_hour: number; start_minute: number; end_hour: number; end_minute: number }>,
): string | undefined => {
  if (!adSchedules || adSchedules.length === 0) return undefined;

  const daySchedules = adSchedules.filter(
    (s) => s.day_of_week === modifier.day_of_week
  );

  if (daySchedules.length === 0) {
    return `Ignored: no ad schedule on ${formatDayOfWeek(modifier.day_of_week)}`;
  }

  const modStart = modifier.start_hour * 60;
  const modEnd = modifier.end_hour * 60;
  let coveredMinutes = 0;

  for (const sched of daySchedules) {
    const schedStart = sched.start_hour * 60 + sched.start_minute;
    const schedEnd = sched.end_hour * 60 + sched.end_minute;
    const overlapStart = Math.max(modStart, schedStart);
    const overlapEnd = Math.min(modEnd, schedEnd);
    if (overlapEnd > overlapStart) {
      coveredMinutes += overlapEnd - overlapStart;
    }
  }

  const totalMinutes = modEnd - modStart;
  if (coveredMinutes === 0) {
    return `Outside ad schedule: ${formatDayOfWeek(modifier.day_of_week)} ${modifier.start_hour}:00-${modifier.end_hour}:00 will be ignored`;
  }
  if (coveredMinutes < totalMinutes) {
    return `Partially outside ad schedule: only the overlapping portion will be applied`;
  }
  return undefined;
};

export const BidAdjustmentsView: React.FC<BidAdjustmentsViewProps> = ({
  campaignId,
  targetLocations,
  disabled = false,
  businessId,
  biddingStrategyType,
}) => {
  const { toast } = useToast();
  const { data: bidModifiers, isLoading } = useQuery(bidModifiersQueryOptions(campaignId));
  const { data: adSchedulesData } = useQuery(adSchedulesQueryOptions(campaignId));
  const { data: proximityRows } = useQuery(proximityBidModifiersQueryOptions(campaignId));

  // Mutations
  const deviceMutation = useDeviceBidModifierMutation(campaignId);
  const locationMutation = useLocationBidModifierMutation(campaignId);
  const scheduleMutation = useScheduleBidModifierMutation(campaignId);
  const deleteDeviceMutation = useDeleteDeviceBidModifierMutation(campaignId);
  const deleteLocationMutation = useDeleteLocationBidModifierMutation(campaignId);
  const deleteScheduleMutation = useDeleteScheduleBidModifierMutation(campaignId);
  const createProximityMutation = useCreateProximityBidModifierMutation(campaignId);
  const deleteProximityMutation = useDeleteProximityBidModifierMutation(campaignId);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ModifierType>('device');
  const [deletingModifier, setDeletingModifier] = useState<UnifiedModifier | null>(null);
  // Set when editing an existing proximity modifier. Google Ads treats the
  // geo target as the criterion identity (lat/lng/radius can't be patched), so
  // edit = create new + delete old. Modal opens pre-filled with the row.
  const [editingProximity, setEditingProximity] = useState<UnifiedModifier | null>(null);
  // Edit-in-modal for device/location/schedule rows (#5868) — identity locked, value editable.
  const [editingModifier, setEditingModifier] = useState<UnifiedModifier | null>(null);

  // Form state
  const [deviceType, setDeviceType] = useState<DeviceType>('MOBILE');
  const [deviceModifier, setDeviceModifier] = useState<number>(0.1);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [locationModifier, setLocationModifier] = useState<number>(0.1);
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>('MONDAY');
  const [startHour, setStartHour] = useState<number>(9);
  const [endHour, setEndHour] = useState<number>(17);
  const [scheduleModifier, setScheduleModifier] = useState<number>(0.1);
  const [proximityShape, setProximityShape] = useState<RadiusFormValue | null>(null);
  const [proximityModifier, setProximityModifier] = useState<number>(0.2);

  // Smart Bidding banner shows above the bid-adjustments view (not per-tab
  // inside the modal) — the rule applies to every modifier on the campaign,
  // so the surface-level banner is the right scope. Google Ads ignores all bid
  // modifiers under Smart Bidding except -100%, which excludes traffic.
  const showSmartBiddingWarning = isSmartBidding(biddingStrategyType);

  // Filter out device modifiers with value 1.0 (no adjustment)
  const deviceModifiers = (bidModifiers?.device || []).filter((d) => d.modifier_value !== 1.0);
  const locationModifiers = bidModifiers?.location || [];
  const scheduleModifiers = bidModifiers?.schedule || [];
  const proximityModifiers = proximityRows || [];

  // Get available options
  const availableDeviceTypes = DEVICE_TYPES.filter(
    (type) => !deviceModifiers.some((d) => d.device_type === type)
  );
  const availableLocations = targetLocations.filter(
    (loc) => !locationModifiers.some((l) => l.location_criteria_id === loc.criteria_id.toString())
  );

  const getLocationName = (criteriaId: string): string => {
    const location = targetLocations.find((l) => l.criteria_id.toString() === criteriaId);
    return location?.name || criteriaId;
  };

  // Combine all modifiers into unified list
  const allModifiers: UnifiedModifier[] = [
    ...deviceModifiers.map((m) => ({
      id: m.criterion_id,
      type: 'device' as ModifierType,
      name: formatDeviceType(m.device_type),
      description: '',
      modifierValue: m.modifier_value,
      rawData: m,
    })),
    ...locationModifiers.map((m) => ({
      id: m.criterion_id,
      type: 'location' as ModifierType,
      name: getLocationName(m.location_criteria_id),
      description: '',
      modifierValue: m.modifier_value,
      rawData: m,
    })),
    ...scheduleModifiers.map((m) => ({
      id: m.criterion_id,
      type: 'schedule' as ModifierType,
      name: `${formatDayOfWeek(m.day_of_week)} ${m.start_hour}:00 - ${m.end_hour}:00`,
      description: '',
      modifierValue: m.modifier_value,
      rawData: m,
      clippingInfo: getScheduleClippingInfo(m, adSchedulesData?.schedules || []),
    })),
    ...proximityModifiers.map((m) => ({
      // Use the DB id as the unified-modifier id — that's what the proximity
      // delete endpoint expects, unlike the other branches which key on
      // googleads_criterion_id.
      id: String(m.id),
      type: 'proximity' as ModifierType,
      name: m.radius_address
        ? m.radius_address
        : `${m.latitude?.toFixed(3) ?? '?'}, ${m.longitude?.toFixed(3) ?? '?'}`,
      description: '',
      modifierValue: m.modifier_value,
      rawData: m,
      // Radius lives outside the (possibly truncated) name so it's always visible.
      secondaryBadge: `${m.radius_km ?? '?'} km`,
    })),
  ];

  const resetForm = () => {
    setDeviceType(availableDeviceTypes[0] || 'MOBILE');
    setDeviceModifier(0.1);
    setSelectedLocationId('');
    setLocationModifier(0.1);
    setDayOfWeek('MONDAY');
    setStartHour(9);
    setEndHour(17);
    setScheduleModifier(0.1);
    setProximityShape(null);
    setProximityModifier(0.2);
  };

  const openAddModal = () => {
    resetForm();
    setEditingProximity(null);
    setEditingModifier(null);
    setActiveTab('device');
    setIsModalOpen(true);
  };

  // Open the modal prefilled for an existing device/location/schedule row (#5868).
  // The save paths are identity-keyed upserts, so a locked identity + new value = update.
  const openEditModal = (modifier: UnifiedModifier) => {
    if (modifier.type === 'proximity') {
      openEditProximityModal(modifier);
      return;
    }
    setEditingProximity(null);
    setEditingModifier(modifier);
    if (modifier.type === 'device') {
      const raw = modifier.rawData as { device_type: DeviceType; modifier_value: number };
      setDeviceType(raw.device_type);
      setDeviceModifier(raw.modifier_value);
      setActiveTab('device');
    } else if (modifier.type === 'location') {
      const raw = modifier.rawData as { location_criteria_id: string; modifier_value: number };
      setSelectedLocationId(raw.location_criteria_id);
      setLocationModifier(raw.modifier_value);
      setActiveTab('location');
    } else {
      const raw = modifier.rawData as { day_of_week: DayOfWeek; start_hour: number; end_hour: number; modifier_value: number };
      setDayOfWeek(raw.day_of_week);
      setStartHour(raw.start_hour);
      setEndHour(raw.end_hour);
      setScheduleModifier(raw.modifier_value);
      setActiveTab('schedule');
    }
    setIsModalOpen(true);
  };

  const openEditProximityModal = (modifier: UnifiedModifier) => {
    const raw = modifier.rawData as {
      latitude: number;
      longitude: number;
      radius_km: number;
      modifier_value: number;
      radius_address: string | null;
    };
    setEditingProximity(modifier);
    setActiveTab('proximity');
    setProximityModifier(raw.modifier_value);
    setProximityShape({
      lat: raw.latitude,
      lng: raw.longitude,
      radiusKm: raw.radius_km,
      countryCode: null,
      formattedAddress: raw.radius_address ?? `${raw.latitude}, ${raw.longitude}`,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProximity(null);
    setEditingModifier(null);
  };

  const handleSave = async () => {
    try {
      if (activeTab === 'device') {
        if (deviceModifier === 0) {
          toast({ description: 'Adjustment of 0% is not valid. Please set a value.', variant: 'destructive' });
          return;
        }
        await deviceMutation.mutateAsync({ device_type: deviceType, modifier_value: deviceModifier });
      } else if (activeTab === 'location') {
        if (!selectedLocationId) {
          toast({ description: 'Please select a location', variant: 'destructive' });
          return;
        }
        if (locationModifier === 0) {
          toast({ description: 'Adjustment of 0% is not valid. Please set a value.', variant: 'destructive' });
          return;
        }
        await locationMutation.mutateAsync({ location_criteria_id: selectedLocationId, modifier_value: locationModifier });
      } else if (activeTab === 'schedule') {
        if (startHour >= endHour) {
          toast({ description: 'Start hour must be before end hour', variant: 'destructive' });
          return;
        }
        if (scheduleModifier === 0) {
          toast({ description: 'Adjustment of 0% is not valid. Please set a value.', variant: 'destructive' });
          return;
        }
        await scheduleMutation.mutateAsync({
          day_of_week: dayOfWeek,
          start_hour: startHour,
          end_hour: endHour,
          modifier_value: scheduleModifier,
        });
      } else if (activeTab === 'proximity') {
        if (!proximityShape) {
          toast({ description: 'Pick a location and radius first.', variant: 'destructive' });
          return;
        }
        if (proximityModifier === 0) {
          toast({ description: 'Adjustment of 0% is not valid. Please set a value.', variant: 'destructive' });
          return;
        }
        const response = await createProximityMutation.mutateAsync({
          latitude: proximityShape.lat,
          longitude: proximityShape.lng,
          radius_km: proximityShape.radiusKm,
          modifier_value: proximityModifier,
          address: proximityShape.formattedAddress || null,
        });
        // Surface backend warnings. The Smart Bidding warning is already shown
        // as a persistent banner above the table, so skip it here — surfacing
        // it again as a destructive toast on a successful save reads as an
        // error and duplicates the same message. Any other (future) warnings
        // fire as a neutral default toast (success, just with context).
        response.warnings?.forEach((warning) => {
          if (warning.code === 'smart_bidding_ignored') return;
          toast({ description: warning.message });
        });
        // Edit mode (delete + recreate). Order: create first, delete second,
        // so a failure leaves a duplicate (recoverable) rather than nothing
        // (irrecoverable spend pause).
        if (editingProximity) {
          try {
            await deleteProximityMutation.mutateAsync(Number(editingProximity.id));
          } catch (delErr) {
            toast({
              description:
                'Saved the new proximity modifier, but couldn\'t delete the old one. Please delete the duplicate manually.',
              variant: 'destructive',
            });
            // Swallow further details so the modal still closes cleanly.
            void delErr;
          }
        }
      }
      setIsModalOpen(false);
      setEditingProximity(null);
      resetForm();
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to save bid adjustment',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingModifier) return;

    try {
      if (deletingModifier.type === 'device') {
        await deleteDeviceMutation.mutateAsync(deletingModifier.id);
      } else if (deletingModifier.type === 'location') {
        await deleteLocationMutation.mutateAsync(deletingModifier.id);
      } else if (deletingModifier.type === 'schedule') {
        await deleteScheduleMutation.mutateAsync(deletingModifier.id);
      } else if (deletingModifier.type === 'proximity') {
        // Proximity rows key on DB id, not GA criterion_id.
        await deleteProximityMutation.mutateAsync(Number(deletingModifier.id));
      }
      setDeletingModifier(null);
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete bid adjustment',
        variant: 'destructive',
      });
    }
  };

  const isSaving =
    deviceMutation.isPending ||
    locationMutation.isPending ||
    scheduleMutation.isPending ||
    createProximityMutation.isPending;
  const isDeleting =
    deleteDeviceMutation.isPending ||
    deleteLocationMutation.isPending ||
    deleteScheduleMutation.isPending ||
    deleteProximityMutation.isPending;

  const columns: ColumnDef<UnifiedModifier, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      meta: { className: 'px-6 w-[30%]' },
      cell: ({ row }) => {
        const modifier = row.original;
        // Cap the wrapper width so long proximity addresses don't force the
        // table to overflow horizontally. The TD has whitespace-nowrap baked in
        // by DataTable and the table doesn't use table-layout: fixed, so the
        // percentage in `meta.className` doesn't actually constrain — the cap
        // has to live on the cell content.
        return (
          <div className={`flex items-center gap-4 max-w-[420px] ${modifier.clippingInfo ? 'opacity-50' : ''}`}>
            {modifier.type === 'device' && getDeviceIcon((modifier.rawData as { device_type: string }).device_type)}
            {modifier.type === 'location' && <MapPin className='h-4 w-4 text-gray-500 flex-shrink-0' />}
            {modifier.type === 'schedule' && <Clock className='h-4 w-4 text-gray-500 flex-shrink-0' />}
            {modifier.type === 'proximity' && <Radar className='h-4 w-4 text-gray-500 flex-shrink-0' />}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='text-sm font-medium text-gray-900 overflow-hidden text-ellipsis min-w-0 cursor-default'>
                    {modifier.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='max-w-[400px] text-xs break-words'>{modifier.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {modifier.secondaryBadge && (
              <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 flex-shrink-0'>
                {modifier.secondaryBadge}
              </span>
            )}
            {modifier.clippingInfo && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertCircle className='h-4 w-4 text-amber-500 flex-shrink-0 opacity-100' />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='max-w-[250px] text-xs'>{modifier.clippingInfo}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      meta: { className: 'px-6 w-[30%]' },
      cell: ({ row }) => {
        const modifier = row.original;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypePillStyle(modifier.type)}`}>
            {getTypeIcon(modifier.type)}
            {modifier.type.charAt(0).toUpperCase() + modifier.type.slice(1)}
          </span>
        );
      },
    },
    {
      accessorKey: 'modifierValue',
      header: 'Adjustment',
      meta: { className: 'pl-6 pr-3' },
      // Read-only formatted percentage (#5868): the previous inline number input wrote
      // to Google on every change; edits now go through the modal via the row pencil.
      cell: ({ row }) => (
        <span className='text-sm font-medium text-gray-900'>
          {formatModifierValue(row.original.modifierValue)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className='flex justify-end gap-1'>
          {(
            <button
              onClick={() => openEditModal(row.original)}
              disabled={disabled}
              aria-label='Edit bid adjustment'
              title='Edit'
              className='h-8 w-8 inline-flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <Pencil className='h-4 w-4' />
            </button>
          )}
          <button
            onClick={() => setDeletingModifier(row.original)}
            disabled={disabled}
            className='h-8 w-8 inline-flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <Trash2 className='h-4 w-4' />
          </button>
        </div>
      ),
      meta: { className: 'pl-2 pr-6 text-right' },
    },
  ];

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>Bid Adjustments</h2>
          <p className='text-sm text-gray-600 mt-1'>Loading bid adjustments...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>Bid Adjustments</h2>
          <p className='text-sm text-gray-600 mt-1'>Adjust bids for different devices, locations, or the time of day.</p>
        </div>
        <Button
          variant='outline'
          className='text-gray-900'
          onClick={openAddModal}
          disabled={disabled}
        >
          <Plus className='h-4 w-4 mr-2' />
          Add Adjustment
        </Button>
      </div>

      {/* Smart Bidding context banner — applies to every bid modifier on the
              campaign (existing rows in the table and any new one added via the
              modal), so it sits at the view level rather than inside the add modal. */}
      {showSmartBiddingWarning && (
        <div className='border-l-4 border-[#FDE68A] bg-[#FEF3C7] text-[#A16207] p-3 rounded-r text-xs flex gap-2 mb-4'>
          <AlertCircle className='h-4 w-4 flex-shrink-0 mt-0.5' />
          <p>
            This campaign's bidding strategy is <strong>{formatBiddingStrategy(biddingStrategyType)}</strong> — a Smart Bidding strategy that ignores bid adjustments. The only adjustment Google Ads will honour is <strong>-100%</strong> (excludes that group entirely). To use other adjustments, change the bidding strategy to a manual one.
          </p>
        </div>
      )}

      {/* Table */}
      <DataTable
        data={allModifiers}
        columns={columns}
        pageSize={999}
        emptyIcon={SlidersHorizontal}
        emptyTitle="No Bid Adjustments"
        emptyDescription="Bids are currently uniform across all devices, locations, and times. Add adjustments to modify bids for specific conditions."
      />

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => (open ? setIsModalOpen(true) : closeModal())}>
        <DialogContent className='sm:max-w-[500px] max-h-[85vh] flex flex-col' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {editingProximity || editingModifier ? 'Edit Bid Adjustment' : 'Add Bid Adjustment'}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs — hidden when editing a specific proximity row (the action is
                  scoped to that one row, switching to Device etc. doesn't make sense). */}
          {!editingProximity && !editingModifier && (
          <div className='flex gap-1 border-b border-gray-200'>
            <button
              onClick={() => setActiveTab('device')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'device'
                  ? 'text-[#290BD9] border-b-2 border-[#290BD9]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className='flex items-center gap-2'>
                <Monitor className='h-4 w-4' />
                Device
              </div>
            </button>
            <button
              onClick={() => setActiveTab('location')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'location'
                  ? 'text-[#290BD9] border-b-2 border-[#290BD9]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className='flex items-center gap-2'>
                <MapPin className='h-4 w-4' />
                Location
              </div>
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'schedule'
                  ? 'text-[#290BD9] border-b-2 border-[#290BD9]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className='flex items-center gap-2'>
                <Clock className='h-4 w-4' />
                Schedule
              </div>
            </button>
            <button
              onClick={() => setActiveTab('proximity')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === 'proximity'
                  ? 'text-[#290BD9] border-b-2 border-[#290BD9]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className='flex items-center gap-2'>
                <Radar className='h-4 w-4' />
                Proximity
              </div>
            </button>
          </div>
          )}

          <div className={`flex-1 overflow-y-auto -mx-2 px-2 ${activeTab === 'proximity' ? 'py-4' : 'py-4 h-[280px]'}`}>
            {/* Device Tab */}
            {activeTab === 'device' && (
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Device Type</Label>
                  <Select
                    value={deviceType}
                    onValueChange={(value) => setDeviceType(value as DeviceType)}
                    disabled={disabled || !!editingModifier}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(editingModifier ? DEVICE_TYPES : availableDeviceTypes).map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatDeviceType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!editingModifier && availableDeviceTypes.length === 0 && (
                    <p className='text-sm text-gray-500'>All device types already have adjustments.</p>
                  )}
                </div>
                <div className='space-y-2'>
                  <Label>Bid Adjustment ({formatModifierValue(deviceModifier)})</Label>
                  <Input
                    type='number'
                    min={-0.9}
                    max={9.0}
                    step={0.1}
                    value={deviceModifier}
                    onChange={(e) => setDeviceModifier(parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    placeholder='0.0'
                  />
                  <p className='text-xs text-gray-500'>Range: -0.9 (-90%) to 9.0 (+900%)</p>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Location</Label>
                  <Select
                    value={selectedLocationId}
                    onValueChange={setSelectedLocationId}
                    disabled={disabled || !!editingModifier}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select a location' />
                    </SelectTrigger>
                    <SelectContent>
                      {(editingModifier ? targetLocations : availableLocations).map((location) => (
                        <SelectItem key={location.criteria_id} value={location.criteria_id.toString()}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!editingModifier && availableLocations.length === 0 && (
                    <p className='text-sm text-gray-500'>
                      {targetLocations.length > 0
                        ? 'All locations already have adjustments.'
                        : 'No target locations configured for this campaign.'}
                    </p>
                  )}
                </div>
                <div className='space-y-2'>
                  <Label>Bid Adjustment ({formatModifierValue(locationModifier)})</Label>
                  <Input
                    type='number'
                    min={-0.9}
                    max={9.0}
                    step={0.1}
                    value={locationModifier}
                    onChange={(e) => setLocationModifier(parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    placeholder='0.0'
                  />
                  <p className='text-xs text-gray-500'>Range: -0.9 (-90%) to 9.0 (+900%)</p>
                </div>
              </div>
            )}

            {/* Schedule Tab */}
            {activeTab === 'schedule' && (
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Day of Week</Label>
                  <Select
                    value={dayOfWeek}
                    onValueChange={(value) => setDayOfWeek(value as DayOfWeek)}
                    disabled={disabled || !!editingModifier}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day) => (
                        <SelectItem key={day} value={day}>
                          {formatDayOfWeek(day)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label>Start Hour (24h)</Label>
                    <Input
                      type='number'
                      min={0}
                      max={23}
                      value={startHour}
                      onChange={(e) => setStartHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                      disabled={disabled || !!editingModifier}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>End Hour (24h)</Label>
                    <Input
                      type='number'
                      min={0}
                      max={24}
                      value={endHour}
                      onChange={(e) => setEndHour(Math.max(0, Math.min(24, parseInt(e.target.value) || 0)))}
                      disabled={disabled || !!editingModifier}
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label>Bid Adjustment ({formatModifierValue(scheduleModifier)})</Label>
                  <Input
                    type='number'
                    min={-0.9}
                    max={9.0}
                    step={0.1}
                    value={scheduleModifier}
                    onChange={(e) => setScheduleModifier(parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    placeholder='0.0'
                  />
                  <p className='text-xs text-gray-500'>Range: -0.9 (-90%) to 9.0 (+900%)</p>
                </div>
              </div>
            )}

            {/* Proximity Tab (#4384) */}
            {activeTab === 'proximity' && (
              <div className='space-y-4'>
                {businessId == null ? (
                  <p className='text-sm text-gray-500'>
                    Attach a business to this campaign to add proximity bid modifiers.
                  </p>
                ) : (
                  <>
                    <RadiusForm
                      // Remount when switching between add and edit so the
                      // form's internal state (selected address, radius input)
                      // re-initialises from initialValue.
                      key={editingProximity?.id ?? 'new'}
                      businessId={businessId}
                      initialValue={
                        editingProximity
                          ? {
                              lat: (editingProximity.rawData as { latitude: number }).latitude,
                              lng: (editingProximity.rawData as { longitude: number }).longitude,
                              radiusKm: (editingProximity.rawData as { radius_km: number }).radius_km,
                              countryCode: null,
                              formattedAddress:
                                (editingProximity.rawData as { radius_address: string | null }).radius_address
                                ?? `${(editingProximity.rawData as { latitude: number }).latitude}, ${(editingProximity.rawData as { longitude: number }).longitude}`,
                            }
                          : null
                      }
                      onChange={setProximityShape}
                      submitting={createProximityMutation.isPending}
                      hideSubmit
                      radiusRowAside={
                        proximityShape ? (
                          <div className='space-y-2'>
                            <Label>
                              Bid Adjustment ({formatModifierValue(proximityModifier)})
                            </Label>
                            <Input
                              type='number'
                              min={-0.9}
                              max={9.0}
                              step={0.1}
                              value={proximityModifier}
                              onChange={(e) => setProximityModifier(parseFloat(e.target.value) || 0)}
                              disabled={disabled}
                              placeholder='0.2'
                            />
                          </div>
                        ) : undefined
                      }
                    />
                  </>
                )}
              </div>
            )}
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={closeModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={disabled || isSaving}
              className='bg-[#290BD9] hover:bg-[#2309C5]'
            >
              {isSaving ? 'Saving...' : editingProximity ? 'Save' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingModifier}
        onClose={() => setDeletingModifier(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Bid Adjustment"
        description={`Are you sure you want to delete the ${deletingModifier?.type} bid adjustment for "${deletingModifier?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
};
